import { motion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const TONY_HERO =
  "https://customer-assets.emergentagent.com/job_tour-innovation-hub/artifacts/bihpxyhe_Tony-Yarto-1-2.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#070707] noise"
    >
      {/* Background magazine grid */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(228,0,124,0.08),transparent_55%)]" />
        <div className="absolute inset-0 bg-grid opacity-30" />
      </div>

      <div className="h-20" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center mt-12 md:mt-16">
          {/* Left: editorial headline */}
          <div className="md:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.3em] text-white/70"
            >
              <Sparkles size={12} className="text-accent" />
              <span data-testid="hero-overline">
                Socio fundador de Incubatour
              </span>
            </motion.div>

            <motion.h1
              data-testid="hero-headline"
              className="font-display mt-10 leading-[0.92] tracking-tighter uppercase"
            >
              <motion.span
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.18 }}
                className="block text-[clamp(2.8rem,7vw,5.8rem)] font-black"
              >
                Locura
              </motion.span>
              <motion.span
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.32 }}
                className="block text-[clamp(2.4rem,6vw,5rem)] font-light italic text-white/55 -mt-1"
              >
                <span className="relative">
                  con método
                  <span
                    aria-hidden
                    className="absolute -bottom-1 left-0 right-0 h-[3px] bg-accent opacity-90"
                  />
                </span>
                .
              </motion.span>
              <motion.span
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.46 }}
                className="block text-[clamp(2.8rem,7vw,5.8rem)] font-black mt-3"
              >
                Turismo
              </motion.span>
              <motion.span
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="block text-[clamp(2.8rem,7vw,5.8rem)] font-black"
              >
                que <span className="text-white/30">transforma</span>.
              </motion.span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.78 }}
              className="mt-10 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
              data-testid="hero-sub"
            >
              <strong className="text-white">Tony Yarto</strong> — provocador,
              consultor y socio fundador de{" "}
              <span className="accent-underline text-white">Incubatour</span>.
              25 años transformando destinos turísticos en{" "}
              <strong className="text-white">México, Colombia y España</strong>{" "}
              con creatividad audaz y modelos de negocio sólidos.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#metodologia"
                data-testid="hero-cta-primary"
                className="btn-block"
              >
                Conoce el método <ArrowDown size={14} />
              </a>
              <a
                href="#contacto"
                data-testid="hero-cta-secondary"
                className="btn-block btn-ghost"
              >
                Trabajemos juntos →
              </a>
            </motion.div>

            {/* Editorial stats stripe */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-14 grid grid-cols-3 gap-8 max-w-xl border-t border-white/10 pt-6"
            >
              {[
                ["25+", "años"],
                ["5,000+", "productos"],
                ["24+", "países"],
              ].map(([k, v]) => (
                <div key={v} className="flex flex-col gap-1">
                  <div className="font-display text-2xl md:text-3xl font-black tracking-tighter">
                    {k}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                    {v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: editorial portrait */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="relative">
              {/* Accent square behind portrait */}
              <div
                aria-hidden
                className="absolute -top-4 -left-4 w-32 h-32 border-2 border-accent z-0"
              />
              <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/15 bg-[#0a0a0a]">
                <img
                  src={TONY_HERO}
                  alt="Tony Yarto"
                  data-testid="hero-portrait"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Subtle vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>

              {/* Editorial tag bottom-right */}
              <div className="absolute -bottom-5 -right-3 bg-black border border-white/15 px-4 py-3 max-w-[220px]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-accent">
                  Founder · Incubatour
                </div>
                <div className="mt-1 font-display text-lg font-bold tracking-tight">
                  Tony Yarto
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-1">
                  Creador de la metodología
                  <br />
                  "Locura con Método"
                </div>
              </div>

              {/* Side caption */}
              <div className="hidden md:block absolute -right-12 top-6 font-mono text-[10px] tracking-widest text-white/40 rotate-180 [writing-mode:vertical-rl]">
                · Retrato · 2026 · MX
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
